<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useApiClient } from "@/api/client";
import { loadingWrapper } from "@/api/helpers/loadingWrapper";
import { toastApiResult } from "@/toaster";
import { titleInputRules } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import AnnotatedEventCategoryDO = App.Data.AnnotatedEventCategoryDO;
import IconPicker from "@/ui/components/IconPicker.vue";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import useDialog from "@/ui/composables/useDialog";

const createEmptyCategory = (): AnnotatedEventCategoryDO => {
	return {
		name: "",
		rgb_color_value: "#000000",
		icon: "fa-light fa-tag",
	};
};

const categories = ref([] as AnnotatedEventCategoryDO[]);
const showAddCategory = ref(false);
const newCategory = ref(createEmptyCategory());
const isFormValid = ref(false);

const tableHeaders = [
	{ title: "ID", key: "id" },
	{ title: "Name", key: "name" },
	{ title: "Actions", key: "actions", align: "end" },
];

const loadCategories = async () => {
	const response = await loadingWrapper(
		useApiClient("annotatedEventCategories").get()
	);

	if (response.isOk()) {
		categories.value = response.value;
	}
};

const createOrUpdateCategory = async () => {
	const request = newCategory.value.id
		? useApiClient("annotatedEventCategories").update(newCategory.value)
		: useApiClient("annotatedEventCategories").create(newCategory.value);

	const response = await toastApiResult(request);

	if (response.isOk()) {
		newCategory.value = createEmptyCategory();
		showAddCategory.value = false;
	}

	await loadCategories();
};

const editCategory = (category: AnnotatedEventCategoryDO) => {
	newCategory.value = {
		...category,
	};

	showAddCategory.value = true;
};

const deleteAnnotation = async (category: AnnotatedEventCategoryDO) => {
	const confirm = await useDialog().confirm({
		title: "Delete Category",
		message: `Are you sure you want to delete: "${category.name}"?`,
		isDelete: true,
	});

	if (!confirm) return;

	await toastApiResult(
		useApiClient("annotatedEventCategories").delete(category.id)
	);

	await loadCategories();
};

onMounted(() => {
	loadCategories();
});
</script>

<template>
	<v-dialog v-model="showAddCategory" scrim="#000000" width="600">
		<v-card>
			<v-toolbar>
				<v-toolbar-title>
					<i class="fa-duotone fa-tags fa-xl mr-3"></i>
					<span v-if="newCategory.id">Update</span>
					<span v-else>Add</span>
					Category
				</v-toolbar-title>
				<v-spacer />

				<v-btn icon @click="showAddCategory = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-card-text>
				<div class="py-2">
					<label class="text-h6">Name:</label>
				</div>
				<v-form v-model="isFormValid">
					<v-text-field
						hide-details="auto"
						variant="filled"
						density="comfortable"
						v-model="newCategory.name"
						:rules="titleInputRules(t)"
						data-testid="category-name"
					/>

					<div class="pb-2 pt-4">
						<label class="text-h6">Category icon:</label>
					</div>
					<div class="mb-2">
						<icon-picker v-model="newCategory.icon" />
					</div>

					<div class="pb-2">
						<label class="text-h6">Color:</label>
					</div>
					<div class="mt-2 d-flex">
						<div><color-picker v-model="newCategory.rgb_color_value" /></div>
					</div>
					<div class="pb-2 pt-4">
						<label class="text-h6">Preview:</label>
					</div>
					<div class="mt-2 d-flex">
						<div>
							<v-chip
								:key="newCategory.icon"
								:prepend-icon="`fa: ${newCategory.icon}`"
								label
								:color="newCategory.rgb_color_value"
							>
								{{ newCategory.name }}
							</v-chip>
						</div>
					</div>
				</v-form>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn
					variant="flat"
					:disabled="isFormValid === false"
					@click="createOrUpdateCategory"
					>{{ newCategory.id ? "Update" : "Create" }}</v-btn
				>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-card class="pa-0 mt-5" elevation="1">
		<div class="d-flex pa-4">
			<v-spacer />
			<v-btn variant="flat" color="primary" @click="showAddCategory = true">
				<i class="fa-duotone fa-plus fa-lg mr-3"></i> Add new category
			</v-btn>
		</div>

		<module-loading>
			<div class="px-4">
				<v-data-table :headers="tableHeaders" :items="categories">
					<template v-slot:item.name="{ item }">
						<v-chip
							label
							:color="item.rgb_color_value"
							:key="item.icon"
							:prepend-icon="`fa: ${item.icon}`"
							data-testid="category-chip"
							>{{ item.name }}</v-chip
						>
					</template>

					<template v-slot:item.actions="{ item }">
						<v-btn
							size="30"
							icon
							color="on-surface"
							variant="text"
							@click="editCategory(item)"
						>
							<i class="fa-light fa-pencil"></i
						></v-btn>
						<v-btn
							size="30"
							icon
							color="error"
							variant="text"
							@click="deleteAnnotation(item)"
							data-testid="delete-category"
						>
							<i class="fa-light fa-trash-can text-error"></i
						></v-btn>
					</template>
				</v-data-table>
			</div>
		</module-loading>
	</v-card>
</template>

<style scoped lang="scss"></style>
